import { TField, TGroup, TSection } from "@smart/bridge-resources-basic";
import { FieldType, validateConditionLink } from "@smart/bridge-types-basic";
import { fromEntries } from "@smart/itops-utils-basic";

type ResponsesType = {
  field: {
    uri: string;
    sectionUri: string;
    groupUri?: string | undefined | null;
  };
};

type CreateHiddenFieldsMapsProps = {
  sections: Pick<TSection, "uri" | "links">[];
  groups: Pick<TGroup, "uri" | "links" | "repeatable">[];
  fields: {
    uri: string;
    type: FieldType;
    links?: TField["links"] | null;
    groupUri?: string | null;
  }[];
  responses: Record<string, any>;
};

const groupLength = ({
  group,
  fieldsInGroup,
  responses,
}: {
  group: Pick<TGroup, "uri" | "links" | "repeatable">;
  fieldsInGroup: Pick<TField, "uri">[];
  responses: Record<string, any>;
}) => {
  if (!group.repeatable) return 1;

  return Math.max(
    ...fieldsInGroup.map((f) =>
      Array.isArray(responses[f.uri]) ? responses[f.uri].length : 1,
    ),
  );
};

const createHiddenMaps = ({
  items,
  responses,
  fieldsMap,
  groups,
}: {
  items: Array<{
    uri: string;
    links?: TField["links"] | TSection["links"] | TGroup["links"] | null;
    groupUri?: string | null;
  }>;
  responses: Record<string, any>;
  fieldsMap: Record<string, Pick<TField, "uri" | "type" | "groupUri">>;
  groups: Pick<TGroup, "uri" | "links" | "repeatable">[];
}) =>
  items.filter((item) => {
    if (!item.links?.length) return false;

    const group = groups.find((g) => g.uri === item.groupUri);

    const matchedConditions = item.links.map((link) => {
      const field = fieldsMap[link.fieldUri];
      if (!field) return true;

      const isRepeatableInGroupLink =
        group?.repeatable && group.uri === field.groupUri;

      if (!isRepeatableInGroupLink) {
        return validateConditionLink({
          conditionLink: link,
          fieldValue: responses[link.fieldUri],
          fieldType: field.type,
          group: groups?.find(
            (g) => field.groupUri && g.uri === field.groupUri,
          ),
        });
      }

      // Check for conditions within a repeatable group
      const length = groupLength({
        group,
        fieldsInGroup: Object.values(fieldsMap).filter(
          (f) => f.groupUri === group.uri,
        ),
        responses,
      });
      // The response for the repeatable field should be visible if it matches any of the conditions.
      return Array.from({ length })
        .map((_, index) =>
          validateConditionLink({
            conditionLink: link,
            fieldValue: responses[link.fieldUri],
            fieldType: field.type,
            group,
            affectingGroupUri: group.uri,
            index,
          }),
        )
        .includes(true);
    });

    return matchedConditions.includes(false);
  });

export const createFilterHiddenFields = ({
  sections,
  groups,
  fields,
  responses,
}: CreateHiddenFieldsMapsProps) => {
  const fieldsMap = fromEntries(
    fields.map((f) => [f.uri, { ...f, groupUri: f.groupUri || undefined }]),
  );
  const hiddenSectionsMap = fromEntries(
    createHiddenMaps({ items: sections, responses, fieldsMap, groups }).map(
      (s) => [s.uri, s],
    ),
  );

  const hiddenGroupsMap = fromEntries(
    createHiddenMaps({ items: groups, responses, fieldsMap, groups }).map(
      (g) => [g.uri, g],
    ),
  );

  const hiddenFieldsMap = fromEntries(
    createHiddenMaps({ items: fields, responses, fieldsMap, groups }).map(
      (f) => [f.uri, f],
    ),
  );
  return (response: ResponsesType) =>
    !hiddenFieldsMap[response.field.uri] &&
    !hiddenSectionsMap[response.field.sectionUri] &&
    !hiddenGroupsMap[response.field.groupUri || ""];
};
